"use client";

import React, { useEffect, useRef, useState } from "react";
import { FileText, CircleCheck, BookOpen, Users, MessageSquare, ShieldAlert, Server, Activity } from "lucide-react";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const inclusions = [{
  title: "Furniture Removal",
  description: "From old sofas to heavy wardrobes, we handle the heavy lifting and responsible disposal of all furniture items.",
  icon: FileText
}, {
  title: "Appliance Removal",
  description: "Safe removal and eco-friendly recycling of refrigerators, washing machines, and other large household appliances.",
  icon: CircleCheck
}, {
  title: "Mattress Disposal",
  description: "Hygienic and efficient mattress removal services, ensuring they are processed at specialized recycling facilities.",
  icon: BookOpen
}, {
  title: "Garage Cleanouts",
  description: "Reclaim your space with our full-service garage clearing, sorting through years of accumulated clutter for you.",
  icon: Users
}, {
  title: "Renovation Debris",
  description: "Post-project cleanup including drywall, flooring, and timber removal to leave your new space pristine.",
  icon: MessageSquare
}, {
  title: "Yard Waste Removal",
  description: "Clearing branches, clippings, and organic debris to keep your outdoor areas looking sharp and well-maintained.",
  icon: ShieldAlert
}, {
  title: "Estate Clearing",
  description: "Compassionate and thorough property clearing services for estates, managing the process with care and respect.",
  icon: Server
}, {
  title: "Commercial Hauling",
  description: "Professional junk removal for offices and retail spaces, including e-waste and old office furniture disposal.",
  icon: Activity
}];
export default function ServicesGrid() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="261045" id="services-grid" className="bg-background text-foreground py-16 md:py-24 lg:py-32">
      <div ref={ref} className={`container mx-auto px-4 md:px-6 transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
        <div className="mb-12 text-center md:mb-16">
          <h2 className="font-default text-foreground mb-4 text-3xl font-bold tracking-tight sm:text-4xl md:text-5xl">
            Our Professional Services
          </h2>
          <p className="text-muted-foreground font-default mx-auto max-w-2xl text-lg">
            We provide comprehensive hauling and junk removal solutions tailored to keep your home and business clutter-free.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {inclusions.map((item, i) => {
          const Icon = item.icon;
          return <Card key={i} data-index={i} className="border-primary bg-card shadow-md transition-shadow hover:shadow-xl border-2 rounded-lg">
                <CardHeader className="pb-4">
                  <div className="bg-secondary mb-4 inline-flex h-12 w-12 items-center justify-center rounded-lg">
                    <Icon className="text-secondary-foreground h-6 w-6" />
                  </div>
                  <CardTitle className="font-default text-foreground text-xl font-semibold">
                    {item.title}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-muted-foreground font-default text-base">
                    {item.description}
                  </CardDescription>
                </CardContent>
              </Card>;
        })}
        </div>
      </div>
    </section>;
}